import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// MUI
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import LinearProgress from "@mui/material/LinearProgress";
import { useTheme, alpha } from "@mui/material/styles";

// MD components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API
import {
  GetUniversitiesCall,
  AddUniversityCall,
  EditUniversityCall,
  DeleteUniversityCall,
  GetUniversityAdminsCall,
  AddUniversityAdminCall,
  RemoveUniversityAdminCall,
  GetAcceptedDomainsCall,
  AddAcceptedDomainCall,
  EditAcceptedDomainCall,
  DeleteAcceptedDomainCall,
} from "../../redux/api/university.api";
import { store } from "../../redux/store";
import { setAuth } from "../../redux/slices/authSlice";
import { openAppConfirm } from "../../components/AppDialogs";
import { startImpersonation } from "../../redux/slices/impersonationSlice";

function Universities() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // university being edited or null

  // Accepted domains dialog (per university)
  const [domainsDialogOpen, setDomainsDialogOpen] = useState(false);
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [domains, setDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [domainsError, setDomainsError] = useState("");
  const [newDomain, setNewDomain] = useState("");
  const [editingDomainId, setEditingDomainId] = useState(null);
  const [editingDomainValue, setEditingDomainValue] = useState("");

  // University admins dialog
  const [adminsDialogOpen, setAdminsDialogOpen] = useState(false);
  const [selectedUniversityForAdmins, setSelectedUniversityForAdmins] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminsError, setAdminsError] = useState("");
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newAdminFirstName, setNewAdminFirstName] = useState("");
  const [newAdminLastName, setNewAdminLastName] = useState("");
  const [addAdminLoading, setAddAdminLoading] = useState(false);

  const [rowMenuAnchor, setRowMenuAnchor] = useState(null);
  const [rowMenuUniversity, setRowMenuUniversity] = useState(null);

  const [form, setForm] = useState({
    name: "",
    short_name: "",
    location: "",
    logo_url: "",
    about: "",
    status: "",
    license_type: "",
    license_start: "",
    license_end: "",
    max_users: "",
    max_profs: "",
    storage_limit_gb: "5",
    private_slides_enabled: false,
  });

  const resetForm = () => {
    setForm({
      name: "",
      short_name: "",
      location: "",
      logo_url: "",
      about: "",
      status: "",
      license_type: "",
      license_start: "",
      license_end: "",
      max_users: "",
      max_profs: "",
      storage_limit_gb: "5",
      private_slides_enabled: false,
    });
    setEditing(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const normalizeDateForInput = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10); // YYYY-MM-DD for <input type="date">
  };

  const openEditDialog = (university) => {
    setForm({
      id: university.id,
      name: university.name || "",
      short_name: university.short_name || "",
      location: university.location || "",
      logo_url: university.logo_url || "",
      about: university.about || "",
      status: university.status || "",
      license_type: university.license_type || "",
      license_start: normalizeDateForInput(university.license_start),
      license_end: normalizeDateForInput(university.license_end),
      max_users: university.max_users ?? "",
      max_profs: university.max_profs ?? "",
      storage_limit_gb:
        university.storage_limit_bytes != null
          ? String(Math.max(1, Math.round(Number(university.storage_limit_bytes) / (1024 * 1024 * 1024))))
          : "5",
      private_slides_enabled: !!university.private_slides_enabled,
    });
    setEditing(university);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const handleChange = (field) => (event) => {
    const value =
      field === "private_slides_enabled"
        ? event.target.checked
        : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const fetchUniversities = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await GetUniversitiesCall();
      const list = Array.isArray(data) ? data : [];
      const withAdmins = await Promise.all(
        list.map(async (uni) => {
          const admins = await GetUniversityAdminsCall(uni.id);
          return { ...uni, admins: Array.isArray(admins) ? admins : [] };
        })
      );
      setUniversities(withAdmins);
    } catch (e) {
      setError("Failed to load universities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUniversities();
  }, []);

  const handleSubmit = async () => {
    if (!form.name) {
      setError("Name is required");
      return;
    }
    setError("");

    const payload = {
      ...form,
      license_start: form.license_start || null,
      license_end: form.license_end || null,
      max_users: form.max_users ? Number(form.max_users) : null,
      max_profs: form.max_profs ? Number(form.max_profs) : null,
      storage_limit_bytes: form.storage_limit_gb
        ? Math.max(1, Number(form.storage_limit_gb)) * 1024 * 1024 * 1024
        : 5 * 1024 * 1024 * 1024,
    };

    if (editing) {
      await EditUniversityCall(payload);
    } else {
      await AddUniversityCall(payload);
    }

    closeDialog();
    await fetchUniversities();
  };

  const handleDelete = async (university) => {
    const confirmed = await openAppConfirm({
      title: "Delete university?",
      message: `Are you sure you want to delete "${university.name}"?`,
      confirmText: "Delete",
      confirmColor: "error",
    });
    if (!confirmed) return;
    await DeleteUniversityCall(university.id);
    await fetchUniversities();
  };

  const openDomainsDialog = async (university) => {
    setSelectedUniversity(university);
    setDomainsDialogOpen(true);
    setNewDomain("");
    setEditingDomainId(null);
    setEditingDomainValue("");
    setDomainsError("");
    setDomainsLoading(true);
    setDomains([]);
    try {
      const data = await GetAcceptedDomainsCall(university.id);
      setDomains(Array.isArray(data) ? data : []);
    } catch (e) {
      setDomains([]);
    } finally {
      setDomainsLoading(false);
    }
  };

  const closeDomainsDialog = () => {
    setDomainsDialogOpen(false);
    setSelectedUniversity(null);
    setDomains([]);
    setNewDomain("");
    setEditingDomainId(null);
    setDomainsError("");
  };

  const handleAddDomain = async () => {
    const domain = (newDomain || "").trim();
    if (!domain || !selectedUniversity) return;
    setDomainsError("");
    const response = await AddAcceptedDomainCall({
      university_id: selectedUniversity.id,
      domain,
    });
    setNewDomain("");
    if (response?.status === 201 && response?.data) {
      setDomains((prev) => [...prev, response.data]);
    } else if (response?.status >= 400) {
      setDomainsError(response?.message || "Failed to add domain");
    }
    const data = await GetAcceptedDomainsCall(selectedUniversity.id);
    setDomains(Array.isArray(data) ? data : []);
  };

  const handleEditDomain = (item) => {
    setEditingDomainId(item.id);
    setEditingDomainValue(item.domain || "");
  };

  const handleSaveEditDomain = async () => {
    if (editingDomainId == null) return;
    await EditAcceptedDomainCall(editingDomainId, {
      domain: editingDomainValue.trim(),
    });
    setEditingDomainId(null);
    setEditingDomainValue("");
    if (selectedUniversity) {
      const data = await GetAcceptedDomainsCall(selectedUniversity.id);
      setDomains(data || []);
    }
  };

  const handleDeleteDomain = async (item) => {
    const confirmed = await openAppConfirm({
      title: "Remove domain?",
      message: `Remove domain "${item.domain}"?`,
      confirmText: "Remove",
      confirmColor: "error",
    });
    if (!confirmed) return;
    await DeleteAcceptedDomainCall(item.id);
    if (selectedUniversity) {
      const data = await GetAcceptedDomainsCall(selectedUniversity.id);
      setDomains(data || []);
    }
  };

  const openAdminsDialog = async (university) => {
    setSelectedUniversityForAdmins(university);
    setAdminsDialogOpen(true);
    setAdmins([]);
    setAdminsError("");
    setNewAdminEmail("");
    setNewAdminFirstName("");
    setNewAdminLastName("");
    setAdminsLoading(true);
    try {
      const data = await GetUniversityAdminsCall(university.id);
      setAdmins(Array.isArray(data) ? data : []);
    } catch (e) {
      setAdmins([]);
      setAdminsError("Failed to load admins");
    } finally {
      setAdminsLoading(false);
    }
  };

  const closeAdminsDialog = () => {
    setAdminsDialogOpen(false);
    setSelectedUniversityForAdmins(null);
    setAdmins([]);
    setAdminsError("");
    setNewAdminEmail("");
    setNewAdminFirstName("");
    setNewAdminLastName("");
  };

  const handleAddUniversityAdmin = async () => {
    const email = (newAdminEmail || "").trim().toLowerCase();
    if (!email || !selectedUniversityForAdmins) return;
    setAdminsError("");
    setAddAdminLoading(true);
    try {
      const res = await AddUniversityAdminCall({
        university_id: selectedUniversityForAdmins.id,
        email,
        first_name: (newAdminFirstName || "").trim() || undefined,
        last_name: (newAdminLastName || "").trim() || undefined,
      });
      if (res?.status === 200 || res?.status === 201) {
        setNewAdminEmail("");
        setNewAdminFirstName("");
        setNewAdminLastName("");
        const data = await GetUniversityAdminsCall(selectedUniversityForAdmins.id);
        setAdmins(Array.isArray(data) ? data : []);
        await fetchUniversities();
      } else {
        setAdminsError(res?.message || "Failed to add admin");
      }
    } catch (e) {
      setAdminsError("Failed to add admin");
    } finally {
      setAddAdminLoading(false);
    }
  };

  const handleRemoveUniversityAdmin = async (admin) => {
    const confirmed = await openAppConfirm({
      title: "Remove university admin?",
      message:
        `Remove "${admin.email}" as university admin? They will keep their account but lose admin access for this university.`,
      confirmText: "Remove",
      confirmColor: "error",
    });
    if (!confirmed) return;
    const res = await RemoveUniversityAdminCall(admin.id);
    if (res?.status === 200 && selectedUniversityForAdmins) {
      const data = await GetUniversityAdminsCall(selectedUniversityForAdmins.id);
      setAdmins(Array.isArray(data) ? data : []);
      await fetchUniversities();
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-2}
                py={2}
                px={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <MDBox py={3}>
                  <MDTypography variant="h6" color="text">
                    Universities
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    Manage university licenses, private slides, and admins.
                  </MDTypography>
                </MDBox>
                <Button
                  variant="contained"
                  color="info"
                  size="small"
                  onClick={openAddDialog}
                  startIcon={<i className="material-icons">add</i>}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  Add university
                </Button>
              </MDBox>

              <MDBox pt={2.5} px={2} pb={2}>
                {loading && (
                  <MDTypography variant="button">Loading...</MDTypography>
                )}
                {error && (
                  <MDTypography variant="button" color="error">
                    {error}
                  </MDTypography>
                )}

                {!loading && (
                  <TableContainer
                    sx={{
                      overflowX: "auto",
                      display: "block",
                      WebkitOverflowScrolling: "touch",
                      maxWidth: "100%",
                    }}
                  >
                    <Table sx={{ minWidth: 720, tableLayout: "fixed" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell align="left" sx={{ width: "10%", whiteSpace: "nowrap" }}>
                          Short Name
                        </TableCell>
                        <TableCell align="left" sx={{ width: "18%", whiteSpace: "nowrap" }}>
                          University admins
                        </TableCell>
                        <TableCell align="center" sx={{ width: "10%", whiteSpace: "nowrap" }}>
                          Max Users
                        </TableCell>
                        <TableCell align="center" sx={{ width: "10%", whiteSpace: "nowrap" }}>
                          Max Profs
                        </TableCell>
                        <TableCell align="left" sx={{ width: "18%", whiteSpace: "nowrap" }}>
                          Storage
                        </TableCell>
                        <TableCell align="center" sx={{ width: "14%", whiteSpace: "nowrap" }}>
                          End License
                        </TableCell>
                        <TableCell align="center" sx={{ width: "10%", whiteSpace: "nowrap" }}>
                          Private Slides
                        </TableCell>
                        <TableCell align="right" sx={{ width: "12%", whiteSpace: "nowrap" }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {universities.map((uni) => {
                        const storagePct = Math.max(0, Math.min(100, Number(uni.storage_percent || 0)));
                        return (
                        <TableRow key={uni.id}>
                          <TableCell align="left" sx={{ width: "10%", whiteSpace: "nowrap" }}>
                            {uni.short_name}
                          </TableCell>
                          <TableCell align="left" sx={{ width: "18%", verticalAlign: "top" }}>
                            {Array.isArray(uni.admins) && uni.admins.length > 0 ? (
                              <MDBox component="span" sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                                {uni.admins.map((a) => (
                                  <MDTypography
                                    key={a.id}
                                    component="span"
                                    variant="caption"
                                    sx={{
                                      display: "inline-block",
                                      px: 0.75,
                                      py: 0.25,
                                      borderRadius: 1,
                                      bgcolor: "action.hover",
                                      color: "text.secondary",
                                      fontSize: "0.75rem",
                                      maxWidth: "100%",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                    title={a.email}
                                  >
                                    {a.first_name || a.last_name
                                      ? `${(a.first_name || "").trim()} ${(a.last_name || "").trim()}`.trim()
                                      : a.email}
                                  </MDTypography>
                                ))}
                              </MDBox>
                            ) : (
                              <MDTypography variant="caption" color="text.secondary">—</MDTypography>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ width: "10%", whiteSpace: "nowrap" }}>
                            {uni.max_users ?? "—"}
                          </TableCell>
                          <TableCell align="center" sx={{ width: "10%", whiteSpace: "nowrap" }}>
                            {uni.max_profs ?? "—"}
                          </TableCell>
                          <TableCell align="left" sx={{ width: "18%" }}>
                            <MDBox sx={{ minWidth: 170, width: "100%" }}>
                              <MDBox display="flex" justifyContent="space-between" mb={0.4}>
                                <MDTypography
                                  variant="caption"
                                  color={storagePct === 0 ? "text.primary" : "text.secondary"}
                                  fontWeight={storagePct === 0 ? 600 : 400}
                                >
                                  {storagePct}%
                                </MDTypography>
                                <MDTypography
                                  variant="caption"
                                  color={storagePct === 0 ? "text.primary" : "text.secondary"}
                                  fontWeight={storagePct === 0 ? 500 : 400}
                                >
                                  {(Number(uni.storage_used_bytes || 0) / (1024 * 1024 * 1024)).toFixed(2)} /{" "}
                                  {(Number(uni.storage_limit_bytes || 0) / (1024 * 1024 * 1024)).toFixed(0)} GB
                                </MDTypography>
                              </MDBox>
                              <LinearProgress
                                variant="determinate"
                                value={storagePct}
                                color={storagePct >= 90 ? "error" : "info"}
                                sx={{
                                  width: "100%",
                                  height: 10,
                                  borderRadius: 999,
                                  boxSizing: "border-box",
                                  bgcolor:
                                    theme.palette.mode === "dark"
                                      ? alpha(theme.palette.common.white, 0.14)
                                      : theme.palette.grey[200],
                                  /* Inset edge instead of border: a border shrinks the padding box so the
                                     colored bar is shorter than the gray track (looks "half height"). */
                                  ...(theme.palette.mode === "light"
                                    ? {
                                        boxShadow: `inset 0 0 0 1px ${theme.palette.divider}`,
                                      }
                                    : {}),
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 999,
                                    top: 0,
                                    bottom: 0,
                                    height: "100%",
                                  },
                                }}
                              />
                            </MDBox>
                          </TableCell>
                          <TableCell align="center" sx={{ width: "14%", whiteSpace: "nowrap" }}>
                            {uni.license_end
                              ? new Date(uni.license_end).toLocaleDateString()
                              : "—"}
                          </TableCell>
                          <TableCell align="center" sx={{ width: "10%", whiteSpace: "nowrap" }}>
                            {uni.private_slides_enabled ? "Enabled" : "Disabled"}
                          </TableCell>
                          <TableCell align="right" sx={{ width: "12%" }} padding="none">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setRowMenuAnchor(e.currentTarget);
                                setRowMenuUniversity(uni);
                              }}
                              sx={{ color: "text.secondary" }}
                              aria-label="Actions"
                            >
                              <i className="material-icons">more_vert</i>
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  </TableContainer>

                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Row actions menu (three dots) */}
      <Menu
        anchorEl={rowMenuAnchor}
        open={Boolean(rowMenuAnchor)}
        onClose={() => {
          setRowMenuAnchor(null);
          setRowMenuUniversity(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            minWidth: 180,
            "& .MuiMenuItem-root": { py: 0.75, px: 1.5 },
            "& .MuiListItemIcon-root": { minWidth: 32 },
          },
        }}
      >
        <MenuItem
          dense
          onClick={() => {
            if (rowMenuUniversity) {
              const currentAuth = store.getState().auth || {};
              const uni = rowMenuUniversity;
              store.dispatch(setAuth({
                ...currentAuth,
                university_id: uni.id,
                universityInfo: { id: uni.id, name: uni.name, short_name: uni.short_name, logo_url: uni.logo_url },
              }));
              store.dispatch(startImpersonation({ university: uni, previousAuth: currentAuth }));
              setRowMenuAnchor(null);
              setRowMenuUniversity(null);
              navigate("/stats");
            }
          }}
        >
          <ListItemIcon sx={{ "& .material-icons": { fontSize: 18 } }}><i className="material-icons">person</i></ListItemIcon>
          <ListItemText primary="Impersonate" primaryTypographyProps={{ fontSize: "0.8125rem", fontWeight: 500 }} />
        </MenuItem>
        <MenuItem
          dense
          onClick={() => {
            if (rowMenuUniversity) openAdminsDialog(rowMenuUniversity);
            setRowMenuAnchor(null);
            setRowMenuUniversity(null);
          }}
        >
          <ListItemIcon sx={{ "& .material-icons": { fontSize: 18 } }}><i className="material-icons">admin_panel_settings</i></ListItemIcon>
          <ListItemText primary="University admins" primaryTypographyProps={{ fontSize: "0.8125rem", fontWeight: 500 }} />
        </MenuItem>
        <MenuItem
          dense
          onClick={() => {
            if (rowMenuUniversity) openDomainsDialog(rowMenuUniversity);
            setRowMenuAnchor(null);
            setRowMenuUniversity(null);
          }}
        >
          <ListItemIcon sx={{ "& .material-icons": { fontSize: 18 } }}><i className="material-icons">link</i></ListItemIcon>
          <ListItemText primary="Accepted domains" primaryTypographyProps={{ fontSize: "0.8125rem", fontWeight: 500 }} />
        </MenuItem>
        <MenuItem
          dense
          onClick={() => {
            if (rowMenuUniversity) openEditDialog(rowMenuUniversity);
            setRowMenuAnchor(null);
            setRowMenuUniversity(null);
          }}
        >
          <ListItemIcon sx={{ "& .material-icons": { fontSize: 18 } }}><i className="material-icons">edit</i></ListItemIcon>
          <ListItemText primary="Edit" primaryTypographyProps={{ fontSize: "0.8125rem", fontWeight: 500 }} />
        </MenuItem>
        <MenuItem
          dense
          onClick={() => {
            if (rowMenuUniversity) handleDelete(rowMenuUniversity);
            setRowMenuAnchor(null);
            setRowMenuUniversity(null);
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ color: "error.main", "& .material-icons": { fontSize: 18 } }}><i className="material-icons">delete</i></ListItemIcon>
          <ListItemText primary="Delete" primaryTypographyProps={{ fontSize: "0.8125rem", fontWeight: 500 }} />
        </MenuItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { bgcolor: "background.card", color: "text.main" } }}>
        <DialogTitle>
          {editing ? "Edit University" : "Add University"}
        </DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Name"
              value={form.name}
              onChange={handleChange("name")}
              fullWidth
              required
            />
            <TextField
              label="Short Name"
              value={form.short_name}
              onChange={handleChange("short_name")}
              fullWidth
            />
            <TextField
              label="Location"
              value={form.location}
              onChange={handleChange("location")}
              fullWidth
            />
            <TextField
              label="Logo URL"
              value={form.logo_url}
              onChange={handleChange("logo_url")}
              fullWidth
            />
            <TextField
              select
              SelectProps={{ native: true }}
              label="Status"
              value={form.status}
              onChange={handleChange("status")}
              fullWidth
            >
              <option value="">Select status</option>
              <option value="active">Active</option>
              <option value="deactive">Deactive</option>
            </TextField>
            <TextField
              select
              SelectProps={{ native: true }}
              label="License Type"
              value={form.license_type}
              onChange={handleChange("license_type")}
              fullWidth
            >
              <option value="">Select license type</option>
              <option value="trial">Trial</option>
              <option value="full">Full</option>
              <option value="custom">Custom</option>
            </TextField>
            <TextField
              label="License Start"
              type="date"
              value={form.license_start}
              onChange={handleChange("license_start")}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="License End"
              type="date"
              value={form.license_end}
              onChange={handleChange("license_end")}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Max Users"
              type="number"
              value={form.max_users}
              onChange={handleChange("max_users")}
              fullWidth
            />
            <TextField
              label="Max Profs"
              type="number"
              value={form.max_profs}
              onChange={handleChange("max_profs")}
              fullWidth
            />
            <TextField
              label="Storage Limit (GB)"
              type="number"
              value={form.storage_limit_gb}
              onChange={handleChange("storage_limit_gb")}
              fullWidth
              inputProps={{ min: 1 }}
            />
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDTypography variant="button">Private slides</MDTypography>
              <Switch
                checked={form.private_slides_enabled}
                onChange={handleChange("private_slides_enabled")}
                color="info"
              />
              <MDTypography variant="caption">
                {form.private_slides_enabled ? "Enabled" : "Disabled"}
              </MDTypography>
            </MDBox>
            <TextField
              label="About"
              value={form.about}
              onChange={handleChange("about")}
              fullWidth
              multiline
              minRows={3}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" color="info" onClick={handleSubmit}>
            {editing ? "Save Changes" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Accepted domains dialog */}
      <Dialog
        open={domainsDialogOpen}
        onClose={closeDomainsDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { bgcolor: "background.card", color: "text.main" } }}
      >
        <DialogTitle>
          Accepted domains
          {selectedUniversity && (
            <MDTypography variant="caption" display="block" color="text">
              {selectedUniversity.name}
            </MDTypography>
          )}
        </DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={2} mt={1}>
            {domainsError && (
              <MDTypography variant="caption" color="error">
                {domainsError}
              </MDTypography>
            )}
            <MDBox display="flex" gap={1} alignItems="center">
              <TextField
                size="small"
                label="New domain"
                placeholder="e.g. example.edu"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddDomain();
                  }
                }}
                fullWidth
              />
              <Button
                variant="contained"
                color="info"
                size="small"
                onClick={handleAddDomain}
              >
                Add
              </Button>
            </MDBox>
            {domainsLoading ? (
              <MDTypography variant="button">Loading...</MDTypography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Domain</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {domains.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {editingDomainId === item.id ? (
                          <TextField
                            size="small"
                            value={editingDomainValue}
                            onChange={(e) =>
                              setEditingDomainValue(e.target.value)
                            }
                            fullWidth
                            autoFocus
                          />
                        ) : (
                          item.domain
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {editingDomainId === item.id ? (
                          <Button
                            size="small"
                            variant="contained"
                            color="info"
                            onClick={handleSaveEditDomain}
                          >
                            Save
                          </Button>
                        ) : (
                          <MDBox display="flex" gap={0.5} justifyContent="flex-end">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleEditDomain(item)}
                            >
                              <i className="material-icons">edit</i>
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteDomain(item)}
                            >
                              <i className="material-icons">delete</i>
                            </IconButton>
                          </MDBox>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {domains.length === 0 && !domainsLoading && (
                    <TableRow>
                      <TableCell colSpan={2}>
                        <MDTypography variant="caption" color="text">
                          No accepted domains yet. Add one above (e.g. polito.it) so users with that email domain can join this university.
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDomainsDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* University admins dialog */}
      <Dialog
        open={adminsDialogOpen}
        onClose={closeAdminsDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: "background.card",
            color: "text.main",
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: "divider", pb: 2 }}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <i className="material-icons" style={{ fontSize: 24, color: "var(--mui-palette-primary-main)" }}>admin_panel_settings</i>
            <span>University admins</span>
          </MDBox>
          {selectedUniversityForAdmins && (
            <MDTypography variant="caption" display="block" color="text.secondary" mt={0.5}>
              {selectedUniversityForAdmins.name}
            </MDTypography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <MDBox display="flex" flexDirection="column" gap={3}>
            {/* Add admin form */}
            <MDBox
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "action.hover",
                border: "1px dashed",
                borderColor: "divider",
              }}
            >
              <MDTypography variant="button" fontWeight="600" gutterBottom>
                Add university admin
              </MDTypography>
              <MDTypography variant="caption" color="text.secondary" display="block" mb={1.5}>
                Enter email. If the user exists, they will be notified. If not, an account will be created and credentials sent by email.
              </MDTypography>
              <MDBox display="flex" flexDirection="column" gap={1.5}>
                <TextField
                  size="small"
                  label="Email"
                  type="email"
                  placeholder="admin@university.edu"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  fullWidth
                  required
                />
                <MDBox display="flex" gap={1.5}>
                  <TextField
                    size="small"
                    label="First name"
                    value={newAdminFirstName}
                    onChange={(e) => setNewAdminFirstName(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="Last name"
                    value={newAdminLastName}
                    onChange={(e) => setNewAdminLastName(e.target.value)}
                    fullWidth
                  />
                </MDBox>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleAddUniversityAdmin}
                  disabled={!newAdminEmail.trim() || addAdminLoading}
                  startIcon={addAdminLoading ? null : <i className="material-icons">person_add</i>}
                >
                  {addAdminLoading ? "Adding…" : "Add admin"}
                </Button>
              </MDBox>
            </MDBox>

            {adminsError && (
              <MDTypography variant="caption" color="error">
                {adminsError}
              </MDTypography>
            )}

            {/* List of admins */}
            <MDTypography variant="button" fontWeight="600">
              Current admins ({admins.length})
            </MDTypography>
            {adminsLoading ? (
              <MDTypography variant="body2" color="text.secondary">Loading…</MDTypography>
            ) : admins.length === 0 ? (
              <MDTypography variant="body2" color="text.secondary">
                No university admins yet. Add one above.
              </MDTypography>
            ) : (
              <MDBox display="flex" flexDirection="column" gap={1}>
                {admins.map((admin) => {
                  const initial = ((admin.first_name || "").charAt(0) + (admin.last_name || "").charAt(0) || admin.email.charAt(0)).toUpperCase();
                  return (
                    <MDBox
                      key={admin.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: "background.default",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <MDBox
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: "0.9rem",
                        }}
                      >
                        {initial}
                      </MDBox>
                      <MDBox flex={1} minWidth={0}>
                        <MDTypography variant="button" fontWeight="600" noWrap>
                          {(admin.first_name && admin.last_name) ? `${admin.first_name} ${admin.last_name}` : admin.email}
                        </MDTypography>
                        <MDTypography variant="caption" color="text.secondary" display="block" noWrap>
                          {admin.email}
                        </MDTypography>
                      </MDBox>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveUniversityAdmin(admin)}
                        title="Remove as admin"
                      >
                        <i className="material-icons">person_remove</i>
                      </IconButton>
                    </MDBox>
                  );
                })}
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: "divider", px: 3, py: 2 }}>
          <Button onClick={closeAdminsDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Universities;
